// src/pages/VerificationCode.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Meta from "../components/Meta";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import kuptoLogo from "../images/logo/kupto logobb-8.png";

import "./styles/verificationCode.css";

// ✅ use your existing app base_url (same as userService)
import { base_url } from "../utils/axiosConfig";

const CODE_LEN = 6;

// ===== helpers =====
const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

/**
 * ✅ Normalize identity into:
 * - email: lowercased
 * - phone: keep digits/+ only (backend will normalize to +256...)
 */
const normalizeIdentity = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return { identity: "", type: "" };

  if (isValidEmail(v)) return { identity: v.toLowerCase(), type: "email" };

  // phone: keep digits and +
  const cleaned = v.replace(/[^\d+]/g, "");
  return { identity: cleaned, type: "phone" };
};

// ===== endpoint helpers (email vs phone) =====
const getSendUrl = (type) => {
  // email flow (sendEmail)
  if (type === "email") return `${base_url}user/send-verification-code`;

  // phone flow (SMS OTP - DB backed)
  return `${base_url}otp/send`;
};

const getVerifyUrl = (type) => {
  if (type === "email") return `${base_url}user/verify-code`;
  return `${base_url}otp/verify`;
};

const VerificationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ identity passed from Identify.jsx
  const stateIdentity = location?.state?.identity || "";
  const stateType = location?.state?.type || "";
  const statePurpose = location?.state?.purpose || "signup";

  // ✅ preserve returnTo (same pattern as Identify)
  const returnToFromState = location?.state?.returnTo;
  const returnTo = useMemo(() => {
    if (typeof returnToFromState === "string" && returnToFromState.trim()) {
      return returnToFromState;
    }
    try {
      const ss = sessionStorage.getItem("kupto_returnTo");
      if (ss && ss.trim()) return ss;
    } catch {}
    return "/";
  }, [returnToFromState]);

  // detect + normalize
  const { identity, type, purpose } = useMemo(() => {
    const base = stateIdentity || "";
    const n = normalizeIdentity(base);

    const finalType = stateType || n.type;
    const finalPurpose = String(statePurpose || "signup").trim() || "signup";

    return {
      identity: base ? n.identity : "",
      type: finalType,
      purpose: finalPurpose,
    };
  }, [stateIdentity, stateType, statePurpose]);

  // ✅ FIX: your route is /identification (not /identify)
  useEffect(() => {
    if (!identity) navigate("/identification", { replace: true });
  }, [identity, navigate]);

  // ✅ preserve returnTo across refresh
  useEffect(() => {
    try {
      if (returnTo && returnTo !== "/") {
        sessionStorage.setItem("kupto_returnTo", returnTo);
      }
    } catch {}
  }, [returnTo]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  // code inputs
  const [codeArr, setCodeArr] = useState(Array(CODE_LEN).fill(""));
  const inputsRef = useRef([]);

  const focusIndex = (i) => {
    const el = inputsRef.current[i];
    if (el) el.focus();
  };

  useEffect(() => {
    const t = setTimeout(() => focusIndex(0), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // resend timer
  const [cooldown, setCooldown] = useState(45);
  useEffect(() => {
    if (!identity) return;
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown, identity]);

  const code = codeArr.join("");
  const codeComplete = code.length === CODE_LEN && !codeArr.includes("");

  const maskedIdentity = useMemo(() => {
    if (!identity) return "";
    if (type === "email") {
      const [u, d] = identity.split("@");
      if (!u || !d) return identity;
      const head = u.slice(0, 1);
      return `${head}${"*".repeat(Math.max(1, u.length - 1))}@${d}`;
    }
    const digits = identity.replace(/[^\d]/g, "");
    if (digits.length < 4) return identity;
    const last = digits.slice(-3);
    return `***${last}`;
  }, [identity, type]);

  const setDigit = (i, val) => {
    const digit = String(val || "").replace(/[^\d]/g, "").slice(0, 1);
    setCodeArr((prev) => {
      const next = [...prev];
      next[i] = digit;
      return next;
    });
    if (digit && i < CODE_LEN - 1) focusIndex(i + 1);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setCodeArr((prev) => {
        const next = [...prev];
        if (next[i]) {
          next[i] = "";
          setTimeout(() => focusIndex(i), 0);
        } else if (i > 0) {
          next[i - 1] = "";
          setTimeout(() => focusIndex(i - 1), 0);
        }
        return next;
      });
      return;
    }

    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusIndex(i - 1);
    }
    if (e.key === "ArrowRight" && i < CODE_LEN - 1) {
      e.preventDefault();
      focusIndex(i + 1);
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/[^\d]/g, "");
    if (!text) return;
    e.preventDefault();

    const slice = text.slice(0, CODE_LEN).split("");
    setCodeArr(() => {
      const next = Array(CODE_LEN).fill("");
      for (let i = 0; i < CODE_LEN; i++) next[i] = slice[i] || "";
      return next;
    });

    const lastFilled = Math.min(slice.length, CODE_LEN) - 1;
    setTimeout(() => focusIndex(Math.max(0, lastFilled)), 0);
  };

  // ✅ IMPORTANT: prevent auto-send running twice (React 18 StrictMode dev)
  const autoSentRef = useRef(false);

  // ✅ SEND CODE (email uses user/*, phone uses otp/*)
  const sendCode = useCallback(
    async ({ silent = false } = {}) => {
      if (!identity || !type) return;

      if (!silent) {
        setApiErr("");
        setInfoMsg("");
      }

      const url = getSendUrl(type);

      const body =
        type === "email"
          ? { identity, type: "email" }
          : { identity, type: "phone", purpose };

      try {
        setIsLoading(true);

        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        let payload = null;
        try {
          payload = await res.json();
        } catch (e) {
          payload = null;
        }

        if (!res.ok) {
          const msg =
            payload?.message ||
            payload?.error ||
            `Failed to send code (${res.status})`;
          throw new Error(msg);
        }

        setIsLoading(false);
        setCooldown(45);

        if (!silent) {
          setInfoMsg(payload?.message || "A new code has been sent.");
        }

        setCodeArr(Array(CODE_LEN).fill(""));
        setTimeout(() => focusIndex(0), 80);
      } catch (err) {
        console.error(err);
        setIsLoading(false);
        setApiErr(err?.message || "Failed to send code. Please try again.");
      }
    },
    [identity, type, purpose]
  );

  // ✅ VERIFY CODE
  const verifyCode = async (e) => {
    e.preventDefault();
    setApiErr("");
    setInfoMsg("");

    if (!codeComplete) {
      setApiErr("Please enter the 6-digit code.");
      return;
    }

    const url = getVerifyUrl(type);

    const body =
      type === "email"
        ? { identity, type: "email", code }
        : { identity, type: "phone", code, purpose };

    try {
      setIsLoading(true);

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch (e) {
        payload = null;
      }

      if (!res.ok) {
        const msg =
          payload?.message ||
          payload?.error ||
          `Verification failed (${res.status})`;
        throw new Error(msg);
      }

      setIsLoading(false);

      const nextRoute =
        payload?.next === "login"
          ? "/login"
          : payload?.next === "signup"
          ? "/signup"
          : "/signup";

      // ✅ FIX: add skipRefresh so OpenRoutes doesn't refresh-cookie and redirect to "/"
      navigate(nextRoute, {
        state: {
          identity,
          type,
          verified: true,
          purpose,
          normalized: payload?.identity || payload?.normalized || "",
          returnTo,
          skipRefresh: true,
        },
        replace: true,
      });
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setApiErr(err?.message || "Invalid or expired code. Please try again.");
      setCodeArr(Array(CODE_LEN).fill(""));
      setTimeout(() => focusIndex(0), 80);
    }
  };

  // ✅ auto-send on mount (ONLY ONCE)
  useEffect(() => {
    if (!identity || !type) return;
    if (autoSentRef.current) return;
    autoSentRef.current = true;

    sendCode({ silent: true });
  }, [identity, type, sendCode]);

  const backReturnTo = useMemo(() => {
    try {
      return returnTo || sessionStorage.getItem("kupto_returnTo") || "/";
    } catch {
      return returnTo || "/";
    }
  }, [returnTo]);

  return (
    <>
      <Meta title="Verification Code" />

      {/* ✅ SAME AS IDENTIFY: fixed, full-width top loader */}
      {isLoading ? (
        <div className="ku-vc-topLoader" role="status" aria-label="Loading">
          <span className="ku-vc-topTrack" aria-hidden="true">
            <span className="ku-vc-topBar" />
          </span>
        </div>
      ) : null}

      {/* ✅ IMPORTANT: do NOT use <Container> here (mobile needs true full screen) */}
      <div className="ku-vc-wrap">
        <div className="ku-vc-shell">
          <div className={`ku-vc-card ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-vc-back ku-vc-back--appOnly"
              onClick={() => navigate(backReturnTo)}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            {/* LEFT */}
            <div className="ku-vc-left">
              <div className="ku-vc-brand" aria-hidden="true">
                <div className="ku-vc-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-vc-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-vc-head">
                <h1 className="ku-vc-title">Enter verification code</h1>
                <p className="ku-vc-sub">
                  We sent a 6-digit code to <b>{maskedIdentity}</b>.
                </p>
              </div>

              <form className="ku-vc-form" onSubmit={verifyCode} noValidate>
                <div className="ku-vc-codeRow" onPaste={onPaste}>
                  {codeArr.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputsRef.current[i] = el)}
                      className="ku-vc-codeInput"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={d}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => onKeyDown(i, e)}
                      maxLength={1}
                      disabled={isLoading}
                      aria-label={`Digit ${i + 1}`}
                    />
                  ))}
                </div>

                {apiErr ? (
                  <div className="ku-vc-error" style={{ marginTop: 10 }}>
                    {apiErr}
                  </div>
                ) : null}

                {infoMsg ? (
                  <div className="ku-vc-info" style={{ marginTop: 10 }}>
                    {infoMsg}
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="ku-vc-btn"
                  disabled={!codeComplete || isLoading}
                >
                  Verify
                </button>

                <div className="ku-vc-resend">
                  {cooldown > 0 ? (
                    <span className="ku-vc-muted">
                      Resend available in <b>{cooldown}s</b>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="ku-vc-linkBtn"
                      onClick={() => sendCode({ silent: false })}
                      disabled={isLoading}
                    >
                      Resend code
                    </button>
                  )}
                </div>

                <p className="ku-vc-legal">
                  Wrong email/phone?{" "}
                  <Link to="/identification">Change it</Link>
                </p>
              </form>

              <span className="ku-vc-glow ku-vc-glow--1" />
              <span className="ku-vc-glow ku-vc-glow--2" />
            </div>

            {/* RIGHT (desktop only, same as Identify behavior) */}
            <div className="ku-vc-right ku-vc-right--desktopOnly">
              <h3 className="ku-vc-rightTitle">Tips</h3>

              <div className="ku-vc-tipList">
                <div className="ku-vc-tip">
                  <div className="ku-vc-tipIcon">⏱</div>
                  <div className="ku-vc-tipTxt">
                    <p className="ku-vc-tipH">Code expires</p>
                    <p className="ku-vc-tipP">
                      If your code expires, tap “Resend code”.
                    </p>
                  </div>
                </div>

                <div className="ku-vc-tip">
                  <div className="ku-vc-tipIcon">📩</div>
                  <div className="ku-vc-tipTxt">
                    <p className="ku-vc-tipH">Check spam</p>
                    <p className="ku-vc-tipP">
                      If email, check spam/junk folders too.
                    </p>
                  </div>
                </div>

                <div className="ku-vc-tip">
                  <div className="ku-vc-tipIcon">📶</div>
                  <div className="ku-vc-tipTxt">
                    <p className="ku-vc-tipH">Network</p>
                    <p className="ku-vc-tipP">
                      Weak network can delay SMS/email delivery.
                    </p>
                  </div>
                </div>

                <div className="ku-vc-tip">
                  <div className="ku-vc-tipIcon">🔐</div>
                  <div className="ku-vc-tipTxt">
                    <p className="ku-vc-tipH">Secure</p>
                    <p className="ku-vc-tipP">
                      Don’t share your code with anyone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* END RIGHT */}
          </div>
        </div>
      </div>
    </>
  );
};

export default VerificationCode;